/**
 * Profile change-password form. Used by Dashboard profile tab.
 */
import React, { memo, useCallback } from 'react';
import { FaLock, FaEye, FaEyeSlash, FaCheckCircle, FaRegCircle } from 'react-icons/fa';
import { useI18n } from '../../contexts/I18n';
import { Card, Error } from './Card';
import { HAS_SPECIAL_CHAR_RE } from '../../utils/passwordPolicy';

const PASSWORD_CRITERIA = [
  { label: '8+ characters', test: (p) => p.length >= 8 && p.length <= 72 },
  { label: 'Uppercase letter', test: (p) => /(?=.*[A-Z])/.test(p) },
  { label: 'Lowercase letter', test: (p) => /(?=.*[a-z])/.test(p) },
  { label: 'Number', test: (p) => /(?=.*\d)/.test(p) },
  { label: 'Special character', test: (p) => HAS_SPECIAL_CHAR_RE.test(p) },
];

const ProfilePasswordForm = memo(function ProfilePasswordForm({
  passwordData,
  setPasswordData,
  passwordErrors,
  setPasswordErrors,
  passwordVisibility,
  setPasswordVisibility,
  setEditPassword,
  msg,
  changingPassword,
  onChangePassword,
  t,
}) {
  const { lang } = useI18n();
  const isRtl = lang === 'ar';
  const rtlTextStyle = isRtl ? { textAlign: 'right', direction: 'rtl', width: '100%', display: 'block' } : {};

  const handleCurrentPasswordChange = useCallback((e) => {
    setPasswordData((prev) => ({ ...prev, currentPwd: e.target.value }));
    setPasswordErrors((prev) => (prev.currentPwd ? { ...prev, currentPwd: null } : prev));
  }, [setPasswordData, setPasswordErrors]);

  const handleNewPasswordChange = useCallback((e) => {
    const newPwd = e.target.value;
    setPasswordData((prev) => {
      const updated = { ...prev, newPwd };
      const errors = {};
      if (!newPwd) errors.newPwd = null;
      else if (newPwd.length < 8) errors.newPwd = 'Must be at least 8 characters';
      else if (!/(?=.*[a-z])/.test(newPwd)) errors.newPwd = 'Must contain lowercase';
      else if (!/(?=.*[A-Z])/.test(newPwd)) errors.newPwd = 'Must contain uppercase';
      else if (!/(?=.*\d)/.test(newPwd)) errors.newPwd = 'Must contain number';
      else if (!HAS_SPECIAL_CHAR_RE.test(newPwd)) errors.newPwd = 'Must contain special char';
      else errors.newPwd = null;
      if (updated.confirmPwd) errors.confirmPwd = newPwd !== updated.confirmPwd ? 'Passwords do not match' : null;
      setPasswordErrors((prev) => ({ ...prev, ...errors }));
      return updated;
    });
  }, [setPasswordData, setPasswordErrors]);

  const handleConfirmPasswordChange = useCallback((e) => {
    const confirmPwd = e.target.value;
    setPasswordData((prev) => {
      const updated = { ...prev, confirmPwd };
      const errors = {};
      if (!confirmPwd) errors.confirmPwd = null;
      else if (confirmPwd !== prev.newPwd) errors.confirmPwd = 'Passwords do not match';
      else errors.confirmPwd = null;
      setPasswordErrors((prev) => ({ ...prev, ...errors }));
      return updated;
    });
  }, [setPasswordData, setPasswordErrors]);

  const handleCancel = useCallback(() => {
    setEditPassword(false);
    setPasswordData({ currentPwd: '', newPwd: '', confirmPwd: '' });
    setPasswordErrors({});
    setPasswordVisibility({ currentPwd: false, newPwd: false, confirmPwd: false });
  }, [setEditPassword, setPasswordData, setPasswordErrors, setPasswordVisibility]);

  const toggleVisibility = useCallback((field) => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  }, [setPasswordVisibility]);

  const renderPasswordField = (field, label, value, onChange, error, autoComplete) => (
    <div className="profile-edit-field">
      <label className={`profile-edit-label ${isRtl ? 'rtl' : ''}`}>
        <FaLock className="profile-edit-field-icon" aria-hidden="true" />
        {label}
        {error ? <span className="field-error-indicator"> *</span> : null}
      </label>
      <div className={`password-input-container ${error ? 'password-input-container-error' : ''}`}>
        <input
          type={passwordVisibility[field] ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          className={`profile-edit-input password-input ${error ? 'profile-edit-input-error' : ''}`}
          placeholder={label}
          autoComplete={autoComplete}
        />
        <button
          type="button"
          className="password-toggle"
          onClick={() => toggleVisibility(field)}
          aria-label={passwordVisibility[field] ? (t('hidePassword') || 'Hide password') : (t('showPassword') || 'Show password')}
        >
          {passwordVisibility[field] ? (
            <FaEyeSlash className="password-toggle-icon" size={18} aria-hidden="true" />
          ) : (
            <FaEye className="password-toggle-icon" size={18} aria-hidden="true" />
          )}
        </button>
      </div>
      {error ? <div className="field-error-message">{error}</div> : null}
    </div>
  );

  return (
    <Card extraClass={`profile-card profile--edit-password ${isRtl ? 'rtl' : ''}`}>
      <div className={`profile-edit-form ${isRtl ? 'rtl' : ''}`}>
        <header className={`profile-edit-header ${isRtl ? 'rtl' : ''}`}>
          <div className={`profile-edit-title-row ${isRtl ? 'rtl' : ''}`}>
            <span className="profile-edit-title-icon-wrap" aria-hidden="true">
              <FaLock className="profile-edit-title-icon" size={18} />
            </span>
            <h2 className={`profile-edit-title ${isRtl ? 'rtl' : ''}`} style={rtlTextStyle}>
              {t('dashChangePassword') || 'Change Password'}
            </h2>
          </div>
          <p className={`profile-edit-subtitle ${isRtl ? 'rtl' : ''}`} style={rtlTextStyle}>
            {t('updatePasswordSubtitle') || 'Update your account password'}
          </p>
        </header>

        {msg.fail && <Error>{msg.fail}</Error>}

        <div className="profile-password-form">
          <div className="profile-edit-grid">
            {renderPasswordField(
              'currentPwd',
              t('currentPassword') || 'Current Password',
              passwordData.currentPwd,
              handleCurrentPasswordChange,
              passwordErrors.currentPwd,
              'current-password'
            )}
            {renderPasswordField(
              'newPwd',
              t('newPassword') || 'New Password',
              passwordData.newPwd,
              handleNewPasswordChange,
              passwordErrors.newPwd,
              'new-password'
            )}
            {passwordData.newPwd ? (
              <div className="password-criteria-container" aria-live="polite">
                <p className="password-criteria-heading">
                  {isRtl ? 'متطلبات كلمة المرور' : 'Password requirements'}
                </p>
                {PASSWORD_CRITERIA.map((c, i) => {
                  const isMet = c.test(passwordData.newPwd);
                  return (
                    <div key={i} className={`password-criterion-item ${isMet ? 'met' : ''}`}>
                      {isMet ? (
                        <FaCheckCircle className="password-criterion-icon met" size={14} aria-hidden="true" />
                      ) : (
                        <FaRegCircle className="password-criterion-icon" size={14} aria-hidden="true" />
                      )}
                      <span className={`password-criterion-text ${isMet ? 'met' : ''}`}>{c.label}</span>
                    </div>
                  );
                })}
              </div>
            ) : null}
            {renderPasswordField(
              'confirmPwd',
              t('confirmNewPassword') || 'Confirm New Password',
              passwordData.confirmPwd,
              handleConfirmPasswordChange,
              passwordErrors.confirmPwd,
              'new-password'
            )}
          </div>
          <div className="profile-edit-actions">
            <button type="button" className="profile-cancel-button" onClick={handleCancel}>
              {t('cancel') || 'Cancel'}
            </button>
            <button
              type="button"
              className={`profile-password-button ${changingPassword ? 'profile-password-button-disabled' : ''}`}
              onClick={onChangePassword}
              disabled={changingPassword}
            >
              {changingPassword ? (t('saving') || 'Updating...') : (t('updatePassword') || 'Update Password')}
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
});

export default ProfilePasswordForm;
